import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Logo } from "@/components/Header";
import { FileDown, Loader2, Search, LogOut, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JWT_KEY = "fundai_jwt";

const CAT_LABEL = { founder: "Founder", investment: "Investment", partner: "Partner", general: "General", grievance: "Grievance" };
const HIDE_KEYS = new Set(["pitch_deck_file_id"]);

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(JWT_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050507] px-6">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] glow-blue" />
      <form onSubmit={submit} className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#0C0C10] p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-6"><Logo subtitle={false} /></div>
        <div className="flex items-center gap-2 mb-6 text-zinc-500"><Lock className="h-4 w-4" /><span className="font-mono text-[11px] uppercase tracking-[0.25em]">Submissions · Secure Login</span></div>
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mb-3 bg-[#08080B] border border-white/10 rounded-lg h-11 px-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#0057FF]" data-testid="admin-email" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full mb-5 bg-[#08080B] border border-white/10 rounded-lg h-11 px-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#0057FF]" data-testid="admin-password" />
        <button type="submit" disabled={busy} className="w-full h-11 bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-lg font-medium transition-colors active:scale-[0.98] flex items-center justify-center" data-testid="admin-login-btn">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
        </button>
      </form>
    </div>
  );
}

export default function AdminInbox() {
  const [jwt, setJwt] = useState(() => localStorage.getItem(JWT_KEY) || "");
  const [authed, setAuthed] = useState(null); // null=checking, true/false
  const [state, setState] = useState({ loading: true, data: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const logout = useCallback(() => {
    localStorage.removeItem(JWT_KEY);
    setJwt("");
    setAuthed(false);
  }, []);

  const load = useCallback(async (token) => {
    setState({ loading: true, data: [] });
    try {
      const res = await axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setState({ loading: false, data: res.data.enquiries });
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); toast.error("Session expired. Please log in again."); }
      else setState({ loading: false, data: [] });
    }
  }, [logout]);

  useEffect(() => {
    document.title = "FundAI Submissions";
    if (jwt) load(jwt);
    else setAuthed(false);
  }, []); // eslint-disable-line

  const onLogin = (token) => { setJwt(token); setAuthed(true); load(token); };

  const filtered = useMemo(() => state.data.filter((d) => {
    if (cat !== "all" && d.enquiry_type !== cat) return false;
    if (!q) return true;
    const hay = `${d.reference} ${d.full_name} ${d.email} ${d.mobile || ""} ${JSON.stringify(d.details || {})}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [state.data, q, cat]);

  if (authed === null) return <div className="min-h-screen flex items-center justify-center bg-[#050507]"><Loader2 className="h-6 w-6 animate-spin text-[#00D1FF]" /></div>;
  if (!authed) return <LoginForm onLogin={onLogin} />;

  return (
    <div className="min-h-screen bg-[#050507]">
      <header className="glass border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4"><Logo subtitle={false} /><span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 hidden sm:block">Submissions</span></div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-zinc-500" data-testid="admin-count">{state.data.length} total</span>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, name, email, details…" className="w-full bg-[#0C0C10] border border-white/10 rounded-lg h-11 pl-10 pr-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-[#0057FF]" data-testid="admin-search" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", "founder", "investment", "partner", "general", "grievance"].map((c) => (
              <button key={c} onClick={() => setCat(c)} className={`text-xs px-3 py-2 rounded-full border transition-colors ${cat === c ? "bg-[#0057FF] border-[#0057FF] text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`} data-testid={`admin-filter-${c}`}>{c === "all" ? "All" : CAT_LABEL[c]}</button>
            ))}
          </div>
        </div>

        {state.loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#00D1FF]" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-zinc-500 text-sm">No submissions match your filters.</p>
        ) : (
          <div className="space-y-4">
            {filtered.map((d) => (
              <div key={d.reference} className="rounded-2xl border border-white/5 bg-[#0C0C10] p-6" data-testid="admin-enquiry-card">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[#00D1FF] text-sm">{d.reference}</span>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-white/10 text-zinc-400">{CAT_LABEL[d.enquiry_type] || d.enquiry_type}</span>
                    <span className="text-[10px] px-2 py-1 rounded-full bg-white/5 text-zinc-400">{d.status}</span>
                  </div>
                  <span className="font-mono text-xs text-zinc-600">{new Date(d.created_at).toLocaleString("en-IN")}</span>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm mb-4">
                  <Info label="Name" value={d.full_name} />
                  <Info label="Email" value={d.email} />
                  <Info label="Mobile" value={d.mobile} />
                  <Info label="Location" value={[d.city, d.state].filter(Boolean).join(", ") || "-"} />
                </div>

                <div className="border-t border-white/5 pt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {Object.entries(d.details || {}).filter(([k]) => !HIDE_KEYS.has(k)).map(([k, v]) => (
                    <div key={k} className="flex gap-2 text-xs">
                      <span className="text-zinc-600 capitalize shrink-0">{k.replace(/_/g, " ")}:</span>
                      <span className="text-zinc-300">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                    </div>
                  ))}
                </div>

                {d.details?.pitch_deck_file_id && (
                  <a href={`${API}/admin/files/${d.details.pitch_deck_file_id}?token=${encodeURIComponent(jwt)}`} className="mt-4 inline-flex items-center gap-2 bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors" data-testid="admin-download-btn">
                    <FileDown className="h-4 w-4" /> Download {d.details.pitch_deck_filename || "attachment"}
                  </a>
                )}

                <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-zinc-600 font-mono">
                  <span>Consent · privacy: {String(d.privacy_accepted)}</span>
                  <span>declarations: {String(d.declarations_accepted)}</span>
                  <span>marketing: {String(d.marketing_consent)}</span>
                  {d.ip_address && <span>ip: {d.ip_address}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><div className="text-zinc-600 text-[10px] uppercase tracking-wider">{label}</div><div className="text-zinc-200 mt-0.5 break-words">{value || "-"}</div></div>
);
